import axios from 'axios';
import baseURL from '../config/config';
import {
  debugEvent,
  debugError,
  getAuthStorageSnapshot,
  isAuthDebugEnabled,
} from './AuthDebug';

import { showSessionTerminatedPopup } from './sessionTerminatedPopup';

const axiosInstance = axios.create({
  baseURL: baseURL,
});

// Debug utility
const debug = (message, data = null) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`🔍 [Axios Debug] ${message}`, data || '');
  }
};

// Feature and settings management
class AppSettings {
  constructor() {
    this.settings = this.loadSettings();
    this.features = this.loadFeatures();
  }

  loadSettings() {
    try {
      const stored = localStorage.getItem('settings');
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error loading settings:', error);
      return {};
    }
  }

  loadFeatures() {
    try {
      const stored = localStorage.getItem('features');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading features:', error);
      return [];
    }
  }

  updateSettings(newSettings) {
    debug('Updating app settings', newSettings);
    this.settings = { ...this.settings, ...newSettings };
    localStorage.setItem('settings', JSON.stringify(this.settings));
  }

  updateFeatures(newFeatures) {
    debug('Updating app features', newFeatures);
    this.features = newFeatures;
    localStorage.setItem('features', JSON.stringify(newFeatures));
  }

  isActivityTrackingEnabled() {
    return this.settings.ACTIVITY_TRACKING !== false; // Default to true if not set
  }

  getFeatureStatus(featureKey) {
    const feature = this.features.find(f => f.key === featureKey);
    return feature ? feature.active : false;
  }
}

const appSettings = new AppSettings();

// Activity tracking system
class ActivityTracker {
  constructor() {
    this.eventQueue = [];
    this.flushTimeout = null;
    this.isEnabled = appSettings.isActivityTrackingEnabled();
    this.initializeEventListeners();
  }

  setEnabled(enabled) {
    debug(`Activity tracking ${enabled ? 'ENABLED' : 'DISABLED'}`);
    this.isEnabled = enabled;
    
    if (!enabled) {
      debug(`Clearing ${this.eventQueue.length} queued events`);
      this.clearQueue();
      this.clearFlushTimer();
    }
  }

  initializeEventListeners() {
    // Reset inactivity timer on user interaction
    ['click', 'touchstart', 'keydown', 'mousemove', 'scroll'].forEach(evt => {
      window.addEventListener(evt, this.resetFlushTimer.bind(this), { passive: true });
    });

    // Flush on page unload
    window.addEventListener('beforeunload', this.flushOnUnload.bind(this));
  }

  resetFlushTimer() {
    if (!this.isEnabled) return;

    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
    }
    this.flushTimeout = setTimeout(() => this.flushEvents(), 10000); // 10s inactivity
  }

  clearFlushTimer() {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }
  }

  clearQueue() {
    this.eventQueue = [];
  }

  trackEvent(event) {
    if (!this.isEnabled) {
      debug('Activity tracking disabled, event ignored', event);
      return;
    }

    debug('Tracking event', event);
    this.eventQueue.push({
      ...event,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });
    this.resetFlushTimer();
  }

  async flushEvents() {
    // Double check enabled status before making any API call
    if (!this.isEnabled) {
      this.clearQueue();
      this.clearFlushTimer();
      debug('Activity tracking disabled, skipping flush');
      return;
    }

    if (this.eventQueue.length === 0) return;

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];
    this.clearFlushTimer();

    try {
      debug('Flushing events to server', eventsToSend);
      await axiosInstance.post('/api/track/track-activity/', eventsToSend);
      debug('Events flushed successfully');
    } catch (error) {
      console.error('Failed to flush events:', error);
      // Only requeue if tracking is still enabled
      if (this.isEnabled) {
        this.eventQueue = [...eventsToSend, ...this.eventQueue];
        debug('Events requeued due to failure');
      } else {
        debug('Events discarded because tracking was disabled during flush');
      }
    }
  }

  flushOnUnload() {
    if (!this.isEnabled || this.eventQueue.length === 0) return;

    try {
      const blob = new Blob([JSON.stringify(this.eventQueue)], { type: 'application/json' });
      navigator.sendBeacon(`${baseURL}/api/track/track-activity/`, blob);
      debug('Events sent via beacon on unload');
    } catch (error) {
      console.error('Beacon failed:', error);
    }
  }
}

const activityTracker = new ActivityTracker();

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Tells the backend this request came from the web app, not the
    // Android app -- used by the per-platform login device limit.
    // See praccel/utils/device_types.py on the backend.
    config.headers['X-Client-Platform'] = 'web';

    debug(`➡️ ${config.method?.toUpperCase()} ${config.url}`, {
      // Never log Authorization headers or request bodies here.
      tokenPresent: !!token,
    });

    if (isAuthDebugEnabled()) {
      debugEvent("AUTH_API_REQUEST", {
        method: config.method?.toUpperCase() || "UNKNOWN",
        url: String(config.url || "").split("?")[0],
        accessTokenPresent: !!token,
      });
    }

    return config;
  },
  (error) => {
    debug('❌ Request interceptor error', error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    debug(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      dataKeys:
        response.data && typeof response.data === "object"
          ? Object.keys(response.data)
          : [],
    });

    if (isAuthDebugEnabled()) {
      debugEvent("AUTH_API_RESPONSE", {
        method: response.config.method?.toUpperCase() || "UNKNOWN",
        url: String(response.config.url || "").split("?")[0],
        httpStatus: response.status,
        applicationStatus: response.data?.status || null,
        dataKeys:
          response.data && typeof response.data === "object"
            ? Object.keys(response.data)
            : [],
        authStorage: getAuthStorageSnapshot(),
      });
    }

    // Check if this is a login/refresh response and update settings
    if (response.data && response.data.data) {
      const responseData = response.data.data;
      
      // Update settings if present in response
      if (responseData.settings) {
        appSettings.updateSettings(responseData.settings);
        activityTracker.setEnabled(appSettings.isActivityTrackingEnabled());
      }
      
      // Update features if present in response
      if (responseData.features) {
        appSettings.updateFeatures(responseData.features);
      }
      
      // Update tokens if present
      if (responseData.access) {
        localStorage.setItem('access_token', responseData.access);
      }
      if (responseData.refresh) {
        localStorage.setItem('refresh_token', responseData.refresh);
      }
    }

    return response;
  },
  async (error) => {
    debug(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
      status: error.response?.status,
      message: error.message,
      // Do not log response bodies; they may contain sensitive data.
    });

    if (isAuthDebugEnabled()) {
      debugEvent("AUTH_API_ERROR", {
        method: error.config?.method?.toUpperCase() || "UNKNOWN",
        url: String(error.config?.url || "").split("?")[0],
        httpStatus: error.response?.status ?? null,
        message: error.message || "Unknown error",
        responseMessage:
          typeof error.response?.data?.message === "string"
            ? error.response.data.message
            : null,
        authStorage: getAuthStorageSnapshot(),
      });
    }

    const originalRequest = error.config;
    
    if (
      error.response &&
      error.response.data &&
      (error.response.data.message === "This session has been terminated. Please log in again." ||
        error.response.data.message === "You do not have permission to perform this action." ||
        error.response.data.message === "Given token not valid for any token type" ||
        error.response.data.message === "Permission Denied" ||
        error.response.data.message === "Authentication Error") &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) throw new Error('No refresh token found');
        
        debug('🔄 Attempting token refresh');

        if (isAuthDebugEnabled()) {
          debugEvent("TOKEN_REFRESH_STARTED", {
            refreshTokenPresent: !!refreshToken,
            authStorage: getAuthStorageSnapshot(),
          });
        }

        const refreshResponse = await axios.post(
          `${baseURL}/api/login/refresh/`,
          { refresh: refreshToken }
        );
        
        const responseData = refreshResponse.data.data;
        const newAccessToken = responseData.access;
        
        // Update localStorage with new data
        localStorage.setItem('access_token', newAccessToken);

        if (isAuthDebugEnabled()) {
          debugEvent("TOKEN_REFRESH_RESPONSE", {
            httpStatus: refreshResponse.status,
            newAccessTokenPresent: !!newAccessToken,
            newRefreshTokenPresent: !!responseData.refresh,
          });
        }
        if (responseData.refresh) {
          localStorage.setItem('refresh_token', responseData.refresh);
        }
        
        // Update settings and features from refresh response
        if (responseData.settings) {
          appSettings.updateSettings(responseData.settings);
          activityTracker.setEnabled(appSettings.isActivityTrackingEnabled());
        }
        if (responseData.features) {
          appSettings.updateFeatures(responseData.features);
        }
        
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        debug('✅ Token refresh successful');
        
        return axiosInstance(originalRequest);
      } catch (refreshError) {
                const refreshStatus = refreshError.response?.status;
        if (
          [400, 401, 403].includes(refreshStatus) ||
          refreshError.message === 'No refresh token found'
        ) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('settings');
          localStorage.removeItem('features');
          activityTracker.clearQueue();
          activityTracker.setEnabled(false);

          showSessionTerminatedPopup();
          return Promise.reject(new Error('Session terminated. Redirecting to login.'));
        }

        debugError("TOKEN_REFRESH_FAILED", refreshError, {
          responseMessage:
            typeof refreshError.response?.data?.message === "string"
              ? refreshError.response.data.message
              : null,
        });
        
        if (
          refreshError.response &&
          refreshError.response.data &&
          (
            (refreshError.response.data.message === "Validation Error" &&
              refreshError.response.data.errors &&
              refreshError.response.data.errors.includes("non_field_errors: This session has been terminated. Please log in again."))
            ||
            (Array.isArray(refreshError.response.data.errors) &&
              (
                refreshError.response.data.errors.includes("Given token not valid for any token type") ||
                refreshError.response.data.errors.includes("token_not_valid") ||
                refreshError.response.data.errors.some(e => typeof e === 'string' && e.includes("Token is invalid or expired"))
              )
            )
          )
        ) {
          let errorMsg = 'Your session has been terminated. Please log in again.';
          if (refreshError.response.data.errors && Array.isArray(refreshError.response.data.errors)) {
            const errorStr = refreshError.response.data.errors.find(e => typeof e === 'string' && (e.includes('non_field_errors') || e.includes('Given token not valid') || e.includes('Token is invalid or expired')))
              || refreshError.response.data.errors[0];
            if (typeof errorStr === 'string' && errorStr.includes(':')) {
              errorMsg = errorStr.split(':').slice(1).join(':').trim();
            } else if (typeof errorStr === 'string') {
              errorMsg = errorStr;
            }
          }
          
          if (isAuthDebugEnabled()) {
            debugEvent("SESSION_TERMINATED_AFTER_REFRESH_FAILURE", {
              authStorageBeforeClear: getAuthStorageSnapshot(),
              httpStatus: refreshError.response?.status ?? null,
              responseMessage:
                typeof refreshError.response?.data?.message === "string"
                  ? refreshError.response.data.message
                  : null,
            });
          }

          // Clear sensitive data
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('settings');
          localStorage.removeItem('features');
          activityTracker.clearQueue();
          activityTracker.setEnabled(false);
          
          if (window.showSessionTerminatedModal) {
            window.showSessionTerminatedModal(errorMsg);
          }
          
          return Promise.reject(new Error('Session terminated. Redirecting to login.'));
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Export activity tracking functions
export const trackEvent = (event) => {
  activityTracker.trackEvent(event);
};

export const flushEvents = () => {
  activityTracker.flushEvents();
};

export const isActivityTrackingEnabled = () => {
  return activityTracker.isEnabled;
};

export const getFeatureStatus = (featureKey) => {
  return appSettings.getFeatureStatus(featureKey);
};

export const getAppSettings = () => {
  return appSettings.settings;
};

// Initialize activity tracking status on module load
activityTracker.setEnabled(appSettings.isActivityTrackingEnabled());

// Expose for global access (optional)
window.flushTrackingEvents = flushEvents;
window.getAppSettings = getAppSettings;
window.getFeatureStatus = getFeatureStatus;

export default axiosInstance;